'use client'

import { useLocale } from 'next-intl'
import { useMemo } from 'react'
import type { LabelType } from '@skop-app/lib-domain/types'

import { Language } from '@/types/translations'
import {
  buildInventoryStatusCatalog,
  type InventoryStatusCatalog,
} from '@/features/inventory/lib/inventory.statusActions'
import type { SupportedKeys, TranslatedOptionItem } from '@/services/property'
import type { SelectOption } from '@/services/property/option.normalized'
import { useListOptions } from '@/services/property/option.hook'
import { findDefaultOption } from '@/services/property/option.defaults'
import type { DeletedOptionIdsByKey } from '@/types/grid'

export type UsePropertiesOptionsResult = {
  definitionVersion: string | undefined
  properties: SupportedKeys[]
  supportedKeys: SupportedKeys[]
  supportedKeysByKey: Record<string, SupportedKeys>

  propertiesOptions: Record<string, TranslatedOptionItem[]>
  selectOptionsByKey: Partial<Record<string, SelectOption[]>>
  displaySelectOptionsByKey: Partial<Record<string, SelectOption[]>>
  actionCatalog: InventoryStatusCatalog
  canCreateInListByKey: Partial<Record<string, boolean>>
  defaultsByKey: Partial<Record<string, LabelType>>

  optionsByID: Record<string, Record<string, TranslatedOptionItem>>

  refDataByKey: Partial<Record<string, Record<string, string>>>
  deletedOptionIdsByKey?: DeletedOptionIdsByKey

  refKey: string
  isCustomProperty: boolean

  isFetching: boolean
  isLoading: boolean
  isSuccess: boolean
}

/**
 * V3: utilise la nouvelle couche "definition + listes"
 * - 1 seule source de vérité: useListOptions (qui utilise useGetProperty V3)
 * - plus de reconstruction manuelle des options/nestedOptions
 * - structure prête pour les updates optimistes ciblées via optionsByID
 */
export const usePropertiesOptions = (
  languageOverride?: Language,
): UsePropertiesOptionsResult => {
  const locale = useLocale()
  const lang = languageOverride ?? (locale.toUpperCase() as Language)

  // V3: tout vient de useListOptions (définition + listes)
  const {
    isPending,
    isReady,
    options,
    optionsByID,
    refDataByKey,
    deletedOptionIdsByKey,
    selectOptions,
    displaySelectOptions,
    canCreateInListByKey,
    supportedKeys,
    supportedKeysByKey,
    refKey,
    definitionVersion,
    isCustomProperty,
  } = useListOptions(lang)

  const properties = supportedKeys

  const propertiesOptions = useMemo(() => {
    const out: Record<string, TranslatedOptionItem[]> = {}
    for (const [key, list] of Object.entries(options)) {
      if (!list) continue
      out[key] = list
    }
    return out
  }, [options])

  const selectOptionsByKey = useMemo(() => {
    const out: Partial<Record<string, SelectOption[]>> = {}
    for (const [key, list] of Object.entries(selectOptions ?? {})) {
      if (!list) continue
      out[key] = list
    }
    return out
  }, [selectOptions])

  const displaySelectOptionsByKey = useMemo(() => {
    const out: Partial<Record<string, SelectOption[]>> = {}
    for (const [key, list] of Object.entries(displaySelectOptions ?? {})) {
      if (!list) continue
      out[key] = list
    }
    return out
  }, [displaySelectOptions])

  const actionCatalog = useMemo(
    () => buildInventoryStatusCatalog(selectOptionsByKey.action),
    [selectOptionsByKey.action],
  )

  const optionsByIDFlat = useMemo(() => {
    const out: Record<string, Record<string, TranslatedOptionItem>> = {}
    for (const [key, mapById] of Object.entries(optionsByID)) {
      if (!mapById) continue
      out[key] = mapById as Record<string, TranslatedOptionItem>
    }
    return out
  }, [optionsByID])

  const defaultsByKey = useMemo(() => {
    const out: Partial<Record<string, LabelType>> = {}
    for (const [key, list] of Object.entries(propertiesOptions)) {
      const found = findDefaultOption(key, list)
      if (found) out[key] = found
    }
    return out
  }, [propertiesOptions])

  return {
    definitionVersion,
    properties,
    supportedKeys,
    supportedKeysByKey,
    propertiesOptions,
    selectOptionsByKey,
    displaySelectOptionsByKey,
    actionCatalog,
    canCreateInListByKey,
    defaultsByKey,
    optionsByID: optionsByIDFlat,
    refDataByKey,
    deletedOptionIdsByKey,
    refKey,
    isCustomProperty,
    isFetching: isPending,
    isLoading: isPending,
    isSuccess: isReady,
  }
}
